import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  Activity,
  User,
  FileText,
  ShieldCheck,
  Bell,
  History,
  ClipboardList,
  LayoutDashboard,
  Siren,
  X,
  Menu,
  Fuel,
  PhoneCall,
  Banknote,
  IndianRupee,
  Tags,
  Receipt,
  MapPin,
  Home,
  Zap,
} from "lucide-react";
import api, { IMAGE_BASE_URL } from "../api/Api";
import { io } from "socket.io-client";
import { toast } from "sonner";
import Swal from "sweetalert2";

// === CSS STYLES ===
const appStyles = `
  /* --- SOS ANIMATIONS --- */
@keyframes sos-pulse-ring {
    0% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); opacity: 1; }
    70% { transform: scale(1); box-shadow: 0 0 0 60px rgba(220, 53, 69, 0); opacity: 0; }
    100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); opacity: 0; }
  }

  @keyframes sos-radar-wave {
    0% { width: 100px; height: 100px; opacity: 0.8; border: 2px solid rgba(220, 53, 69, 0.8); }
    100% { width: 300px; height: 300px; opacity: 0; border: 2px solid rgba(220, 53, 69, 0); }
  }
  
  .sos-radar-circle {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    box-sizing: border-box;
    animation: sos-radar-wave 2s linear infinite;
    z-index: 0;
  }
  
  .sos-icon-container {
    animation: sos-pulse-ring 2s infinite;
  }

  .sos-pulse-always { background-color: #dc3545 !important; animation: radar-pulse 1.5s infinite; }
  @keyframes radar-pulse {
    0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
    100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
  }

  /* --- SIDEBAR STYLES --- */
  .sidebar-backdrop {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0, 0, 0, 0.5); z-index: 1040;
    opacity: 0; visibility: hidden; transition: opacity 0.3s ease;
  }
  .sidebar-backdrop.show { opacity: 1; visibility: visible; }
  
  .sidebar-drawer {
    position: fixed; top: 0; left: 0; width: 280px; height: 100%;
    background: #ffffff; z-index: 1050;
    box-shadow: 4px 0 24px rgba(0,0,0,0.15);
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
    display: flex; flex-direction: column;
  }
  .sidebar-drawer.open { transform: translateX(0); }
  
  .sidebar-link {
    display: flex; align-items: center; padding: 14px 20px;
    color: #495057; text-decoration: none; font-weight: 500;
    transition: all 0.2s; border-radius: 8px;
    margin: 4px 10px;
  }
  .sidebar-link:hover { background: #f8f9fa; color: #0d6efd; }
  .sidebar-link.active {
    background: #e7f1ff; color: #0d6efd; font-weight: 600;
  }
    /* --- NOTIFICATION DRAWER ANIMATIONS --- */
    /* --- PREMIUM EMPTY STATE ANIMATION --- */
  @keyframes float-bell {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-6px); }
    100% { transform: translateY(0px); }
  }
  @keyframes slide-in-right {
    0% { transform: translateX(100%); }
    100% { transform: translateX(0); }
  }
  @keyframes fade-in-backdrop {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
`;

const socket = io(IMAGE_BASE_URL, {
  transports: ["websocket"],
  autoConnect: false,
});

const HeaderLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const driverRegNo = localStorage.getItem("driverRegNo");

  // --- STATES ---
  const [showSosModal, setShowSosModal] = useState(false);
  const [sosCountdown, setSosCountdown] = useState(3);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sosTimerRef = useRef(null);

  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // --- LOGIC STATES ---
  const [activeTripId, setActiveTripId] = useState(null);
  const [isPunchedIn, setIsPunchedIn] = useState(false);
  const [isDutyStarted, setIsDutyStarted] = useState(false);

  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEndX(null); // Reset end position on new touch
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;

    const distance = touchStartX - touchEndX;
    const isRightSwipe = distance < -50; // Requires at least a 50px swipe to the right

    if (isRightSwipe) {
      setShowNotifications(false); // Close the modal!
    }
  };

  // --- PROFILE STATE ---
  const [profile, setProfile] = useState({
    full_name: "",
    phone: "",
    driver_photo: null,
  });
  // const socket = io(IMAGE_BASE_URL);

  // Inject Styles
  useLayoutEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = appStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // --- API CALLS ---
  useEffect(() => {
    if (!driverRegNo) return;

    //  $$$$$$$$$$$$$$$$#############@@@@@@@@@@@@@@@@@@  new
    const fetchAssignedTrip = async () => {
      try {
        const res = await api.get(
          `/driver/assigned-trip?driver_regno=${driverRegNo}`,
        );
        const tripData = Array.isArray(res.data) ? res.data : [res.data];
        // console.log("Assigned Trip Data:", tripData);
        if (tripData.length > 0 && tripData[0]?.enq_id) {
          setActiveTripId(tripData[0].enq_id);
        } else {
          setActiveTripId(null);
        }
      } catch (err) {
        setActiveTripId(null);
      }
    };

    // 2. Fetch Attendance (Today)
    const fetchAttendance = async () => {
      try {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, "0");
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const year = today.getFullYear();

        const res = await api.get(
          `/attendanceCheckDate/${driverRegNo}/${day}/${month}/${year}`,
        );
        const data = res.data?.data || [];

        // Check if user is currently punched in (ck_sts usually 1 for IN)
        const punchedIn = data.length > 0 && Number(data[0].ck_sts) === 1;
        // console.log("Attendance Data:", data, "Is Punched In:", punchedIn);
        setIsPunchedIn(punchedIn);
      } catch (err) {
        console.error("Attendance fetch failed", err);
      }
    };

    // 3. Fetch Duty Status
    const fetchDutyStatus = async () => {
      try {
        const localDate = new Date().toLocaleDateString("en-CA");
        const res = await api.get(
          `/duty/status/${driverRegNo}?date=${localDate}`,
        );
        const statusFromApi = res.data?.dutyStatus;
        // console.log("Duty Status from API:", statusFromApi);
        setIsDutyStarted(Number(statusFromApi) === 1);
      } catch (err) {
        console.error("Duty status fetch failed", err);
      }
    };

    // 4. Fetch Profile
    const fetchProfileDetails = async () => {
      try {
        const res = await api.get("/auth/profile");
        setProfile(res.data);
      } catch (err) {
        console.error("Profile sync failed");
      }
    };

    // Execute all
    fetchAssignedTrip();
    fetchAttendance();
    fetchDutyStatus();
    fetchProfileDetails();
  }, [driverRegNo, location.pathname]);

  //  CRON JOB SOCKET LISTENERS & CONNECTION MANAGEMENT

  // 1. Handle Connection and Global Listeners (Runs ONCE on mount)
  useEffect(() => {
    // Only connect if not already connected
    if (!socket.connected) {
      socket.connect();
    }

    //  9:00 AM ATTENDANCE REMINDER
    const handleAttendanceReminder = (data) => {
      setNotifications((prev) => [
        {
          id: Date.now(),
          title: data.title,
          message: data.message,
          time: new Date(),
          isRead: false,
          type: "attendance",
        },
        ...prev,
      ]);

      Swal.fire({
        html: `
          <div class="d-flex flex-column align-items-center text-center mt-2 pb-1">
            <div class="rounded-circle d-flex align-items-center justify-content-center mb-3 shadow-sm" 
                 style="width: 64px; height: 64px; background: rgba(255,255,255,0.2); backdrop-filter: blur(8px);">
              <span style="font-size: 30px;">⏰</span>
            </div>
            <h4 class="fw-bold text-white mb-2" style="letter-spacing: 0.5px;">${data.title}</h4>
            <p class="text-white text-opacity-75 mb-0" style="font-size: 15px; line-height: 1.4;">${data.message}</p>
          </div>
        `,
        // Premium deeper purple/pink gradient
        background: "linear-gradient(135deg, #662d8c 0%, #ed1e79 100%)",
        color: "#ffffff",
        showCancelButton: true,
        confirmButtonText: "Punch Now",
        cancelButtonText: "Dismiss",
        buttonsStyling: false,
        allowOutsideClick: false,
        backdrop: "rgba(0,0,0,0.65)", // Slightly darker backdrop for focus
        width: "340px", // Fixed mobile-friendly width
        padding: "1.5rem",
        customClass: {
          popup: "rounded-4 shadow-lg border-0", // Native iOS style rounding
          actions: "d-flex gap-3 w-100 mt-4 mb-0", // Perfect side-by-side spacing
          confirmButton:
            "btn flex-grow-1 bg-white text-dark fw-bold rounded-pill py-2 shadow-sm",
          cancelButton:
            "btn flex-grow-1 bg-transparent text-white border border-white border-opacity-25 rounded-pill py-2",
        },
        timer: 60000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(`/dashboard`);
        }
      });
    };

    // 🚗 LISTENER: 45-MINUTE TRIP REMINDER
    const handleTripReminder = (data) => {
      setNotifications((prev) => [
        {
          id: Date.now(),
          title: "Upcoming Trip!",
          message: data.message,
          time: new Date(),
          isRead: false,
          type: "trip",
          bookingId: data.bookingId,
        },
        ...prev,
      ]);

      Swal.fire({
        html: `
          <div class="d-flex flex-column align-items-center text-center mt-2 pb-1">
            <div class="rounded-circle d-flex align-items-center justify-content-center mb-3 shadow-sm" 
                 style="width: 64px; height: 64px; background: rgba(255,255,255,0.2); backdrop-filter: blur(8px);">
              <span style="font-size: 30px;">🚗</span>
            </div>
            <h4 class="fw-bold text-white mb-2" style="letter-spacing: 0.5px;">Upcoming Trip!</h4>
            <p class="text-white text-opacity-75 mb-0" style="font-size: 15px; line-height: 1.4;">${data.message}</p>
          </div>
        `,
        // Premium deep orange/red gradient
        background: "linear-gradient(135deg, #FF512F 0%, #F09819 100%)",
        color: "#ffffff",
        showCancelButton: true,
        confirmButtonText: "View Trip",
        cancelButtonText: "Dismiss",
        buttonsStyling: false,
        allowOutsideClick: false,
        backdrop: "rgba(0,0,0,0.65)",
        width: "340px",
        padding: "1.5rem",
        customClass: {
          popup: "rounded-4 shadow-lg border-0",
          actions: "d-flex gap-3 w-100 mt-4 mb-0",
          confirmButton:
            "btn flex-grow-1 bg-white text-dark fw-bold rounded-pill py-2 shadow-sm",
          cancelButton:
            "btn flex-grow-1 bg-transparent text-white border border-white border-opacity-25 rounded-pill py-2",
        },
        timer: 30000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed && data.bookingId) {
          navigate(`/my-trips/${data.bookingId}`);
        }
      });
    };

    socket.on("attendance_reminder", handleAttendanceReminder);
    socket.on("trip_reminder", handleTripReminder);

    return () => {
      socket.off("attendance_reminder", handleAttendanceReminder);
      socket.off("trip_reminder", handleTripReminder);
      // socket.disconnect();
    };
  }, [navigate]); // Notice 'profile' is no longer here!

  // 2. Handle Profile Validation & Room Joining (Runs when profile updates)
  useEffect(() => {
    const numericDriverId =
      localStorage.getItem("driverId") || profile?.id || profile?.driver_id;

    if (
      numericDriverId &&
      numericDriverId !== "undefined" &&
      numericDriverId !== "null"
    ) {
      // Ensure the connection is open before trying to emit
      if (!socket.connected) {
        socket.connect();
      }
      socket.emit("join_app_session", `global_driver_${numericDriverId}`);
    } else {
      console.log("⏳ [SOCKET] Still waiting for Driver ID...");
    }
  }, [profile]); // This only triggers when the profile state changes

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // --- NAVIGATION LISTS ---
  const sidebarItems = [
    { label: "Home", path: "/dashboard", icon: Home },
    {
      label: "My Trips",
      path: activeTripId ? `/my-trips/${activeTripId}` : "/my-trips",
      icon: ClipboardList,
    },
    { label: "Activity", path: "/driver-activity", icon: Activity },

    { label: "Refuel", path: "/refuel", icon: Fuel },
    { label: "Other Expense", path: "/other-expense", icon: IndianRupee },
    { label: "DMR", path: "/dmr", icon: FileText },

    { label: "Attendance", path: "/attendance-history", icon: History },
    { label: "Documents", path: "/documents", icon: FileText },

    { label: "Profile", path: "/profile", icon: User },
    { label: "Reset MPIN", path: "/reset-mpin", icon: ShieldCheck },

    { label: "Contact Office", path: "/contact-office", icon: PhoneCall },
  ];

  const bottomNavItems = [
    { label: "Home", path: "/dashboard", icon: LayoutDashboard },
    {
      label: "Trip",
      path: activeTripId ? `/my-trips/${activeTripId}` : "/my-trips",
      icon: ClipboardList,
    },
    {
      label: "Expenses",
      path: "/actions-hub",
      icon: Receipt,
    },
    { label: "Activity", path: "/driver-activity", icon: Activity },
    { label: "Profile", path: "/profile", icon: User },
  ];

  // --- RESTRICTED NAVIGATION HANDLER ---
  const handleRestrictedNavigate = (item) => {
    if (item.label === "My Trips" || item.label === "Trip") {
      if (!activeTripId) {
        toast.error(
          "No active trip assigned at the moment. Please check with the office.",
        );
        return;
      }
      if (!isPunchedIn || !isDutyStarted) {
        Swal.fire({
          icon: "warning",
          title: "Restricted Access",
          text: "Please submit your attendance first and start duty to go for trip",
          confirmButtonColor: "#5900ff",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          width: "280px",
          padding: "1.25rem 1rem 1rem",
          customClass: {
            popup: "rounded-4 shadow-lg border-0",
            icon: "m-0 mx-auto",
            title: "fs-6 fw-bold mt-3 mb-1",
            htmlContainer: "m-0 mb-3 small text-muted",
            actions: "w-100 m-0 p-0",
            confirmButton: "w-100 rounded-pill py-2 m-0 fw-bold",
          },
          backdrop: "rgba(0,0,0,0.6)",
        });
        return;
      }
    }
    setIsSidebarOpen(false);
    navigate(item.path);
  };

  // --- LOGOUT LOGIC ---
  const handleLogout = () => {
    toast.dismiss();
    setIsSidebarOpen(false);
    toast(
      (t) => (
        <div
          className="bg-white rounded-3 shadow-lg p-3"
          style={{ minWidth: "300px", borderLeft: "6px solid #ef4444" }}
        >
          <div className="d-flex align-items-start mb-3">
            <div className="me-3 mt-1 text-danger">
              <LogOut size={24} />
            </div>
            <div>
              <h6 className="fw-bold text-dark mb-1">Sign Out?</h6>
              <p className="text-muted mb-0 small">
                Are you sure you want to end your current session?
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-light flex-grow-1 fw-semibold border"
              onClick={() => toast.dismiss(t)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger flex-grow-1 fw-semibold shadow-sm"
              onClick={() => {
                localStorage.removeItem("driverToken");
                localStorage.removeItem("driverRegNo");
                localStorage.removeItem("driverMobile");
                toast.dismiss(t);
                toast.success("Logged out successfully");
                setTimeout(() => navigate("/login", { replace: true }), 500);
              }}
            >
              Logout
            </button>
          </div>
        </div>
      ),
      {
        duration: 3000,
        position: "top-center",
        style: { background: "transparent", border: "none", padding: 0 },
      },
    );
  };

  // --- SOS LOGIC ---
  useEffect(() => {
    let interval = null;
    if (showSosModal && sosCountdown > 0) {
      interval = setInterval(() => setSosCountdown((prev) => prev - 1), 1000);
      sosTimerRef.current = interval;
    } else if (showSosModal && sosCountdown === 0) {
      clearInterval(interval);
      setShowSosModal(false);
      handleTriggerSosApi();
    }
    return () => clearInterval(interval);
  }, [showSosModal, sosCountdown]);

  const handleTriggerSosApi = async () => {
    if (navigator.geolocation) {
      // 1. Get Coordinates (Required)
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          // Show loading
          const loadingToast = toast.loading("Sending Emergency Alert...");

          try {
            await api.post("/sos/trigger", {
              driverId: driverRegNo,
              latitude,
              longitude,
            });

            // 3. Success!
            toast.dismiss(loadingToast);
            toast.success("SOS SIGNAL SENT TO ADMIN!", {
              style: { background: "#dc3545", color: "#fff" },
              icon: <Siren size={20} color="white" />,
            });
          } catch (err) {
            toast.dismiss(loadingToast);
            toast.error("SOS FAILED! CALL OFFICE.");
          }
        },
        (error) => {
          // Handle GPS errors specifically
          toast.error("GPS Error: Could not get location.");
        },
        // 4. GPS Options: High accuracy, but fail fast (10s max)
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    } else {
      toast.error("Geolocation not supported");
    }
  };

  const handleSosTrigger = () => {
    setShowSosModal(true);
    setSosCountdown(3);
  };
  const handleCancelSos = () => {
    setShowSosModal(false);
    setSosCountdown(3);
    if (sosTimerRef.current) clearInterval(sosTimerRef.current);
    toast.success("Alert Cancelled");
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-white">
      {/* === 1. SIDEBAR (DRAWER) === */}
      <div
        className={`sidebar-backdrop ${isSidebarOpen ? "show" : ""}`}
        onClick={() => setIsSidebarOpen(false)}
      ></div>
      <div className={`sidebar-drawer ${isSidebarOpen ? "open" : ""}`}>
        {/* === SIDEBAR HEADER (Correct Profile Design) === */}
        <div
          className="p-3 mb-2 d-flex flex-column align-items-center justify-content-center text-center"
          style={{
            background: "linear-gradient(135deg, #fd7e14 0%, #ff9234 100%)",
            borderRadius: "0 0 24px 0",
            boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
            position: "relative",
            overflow: "hidden",
            paddingTop: "max(env(safe-area-inset-top), 45px) !important",
          }}
        >
          {/* Logo Name */}
          <div className="mb-3" onClick={() => navigate("/dashboard")}>
            <span
              className="fw-bold text-white lh-1"
              style={{ fontSize: "1.8rem" }}
            >
              P
            </span>
            <span
              className="fw-bold text-white lh-1"
              style={{ fontSize: "1rem", marginLeft: "-1px" }}
            >
              ATRA
            </span>
            <span
              className="fw-semibold text-dark opacity-75 lh-1"
              style={{ fontSize: "1rem", marginLeft: "6px" }}
            >
              TRAVELS PVT. LTD.
            </span>
          </div>

          {/* Profile Photo */}
          <div className="mb-2 position-relative">
            <div
              className="bg-success rounded-circle p-1 shadow-sm d-flex align-items-center justify-content-center"
              style={{ width: "80px", height: "80px", overflow: "hidden" }}
            >
              {profile.driver_photo ? (
                <img
                  src={`${IMAGE_BASE_URL}${profile.driver_photo}`}
                  alt="Driver"
                  className="rounded-circle"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                /* --- CHANGED THIS PART --- */
                <span
                  className="text-white fw-bold"
                  style={{ fontSize: "2.5rem" }}
                >
                  {profile.fullName
                    ? profile.fullName.charAt(0).toUpperCase()
                    : "D"}
                </span>
              )}
            </div>
          </div>

          {/* Driver Name & ID */}
          <div className="text-white">
            <h6
              className="fw-bold mb-0 text-white text-uppercase"
              style={{ letterSpacing: "0.5px" }}
            >
              {profile.fullName || "Unknown Driver"}
            </h6>
            <span
              className="opacity-85 text-primary fw-bold"
              style={{ fontSize: "0.9rem" }}
            >
              {driverRegNo || "N/A"}
            </span>
          </div>

          {/* Decorative Circle */}
          <div
            style={{
              position: "absolute",
              top: "-20px",
              left: "-20px",
              width: "100px",
              height: "100px",
              background: "white",
              opacity: "0.1",
              borderRadius: "50%",
            }}
          ></div>
        </div>

        {/* Sidebar Links */}
        <div className="py-1 flex-grow-1 overflow-auto">
          {sidebarItems.map((item) => (
            <div
              key={item.path}
              onClick={() => handleRestrictedNavigate(item)}
              className={`sidebar-link cursor-pointer d-flex align-items-center p-2 mb-1 rounded-3 ${
                isActive(item.path.split("/")[1])
                  ? "active bg-info text-white shadow-sm"
                  : "text-dark"
              }`}
              style={{ transition: "all 0.2s ease" }}
            >
              <item.icon
                size={20}
                className={`me-3 ${
                  isActive(item.path.split("/")[1])
                    ? "opacity-100"
                    : "opacity-75"
                }`}
              />
              <span className="fw-semibold">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Sidebar Footer */}
        <div className="p-3 border-top">
          <button
            onClick={handleLogout}
            className="btn btn-light w-100 d-flex align-items-center justify-content-center text-danger fw-bold"
          >
            <LogOut size={18} className="me-2" /> Logout
          </button>
        </div>
      </div>

      {/* === IMPROVED SOS MODAL === */}
      {showSosModal && (
        <div
          className="position-fixed top-0 bottom-0 start-0 end-0 w-100 d-flex flex-column align-items-center justify-content-between overflow-hidden"
          style={{
            background:
              "radial-gradient(circle at center, #2a0808 0%, #050505 100%)",
            zIndex: 10000,
            backdropFilter: "blur(10px)",
            paddingTop: "max(env(safe-area-inset-top), 60px)",
            paddingBottom: "max(env(safe-area-inset-bottom), 30px)",
          }}
        >
          {/* Custom Animations */}
          <style>{`
            @keyframes pulseRadar {
              0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.8; }
              100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
            }
            @keyframes heartbeat {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.08); }
            }
            @keyframes blinkText {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            .sos-radar-circle {
              position: absolute;
              top: 50%;
              left: 50%;
              width: 110px;
              height: 110px;
              background: transparent;
              border: 2px solid rgba(239, 68, 68, 0.6);
              border-radius: 50%;
              animation: pulseRadar 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
            }
            .sos-core-icon {
              animation: heartbeat 1.5s ease-in-out infinite;
            }
            .sos-warning-text {
              animation: blinkText 1s infinite;
            }
            .btn-cancel-sos {
              background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
              border: none;
              box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
              transition: all 0.2s ease;
            }
            .btn-cancel-sos:active {
              transform: scale(0.95);
              box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
            }
          `}</style>

          {/* 1. TOP: GLOWING COUNTDOWN NUMBER */}
          <div className="text-center mt-2">
            <h1
              className="fw-black m-0"
              style={{
                fontSize:
                  "min(6.5rem, 25vw)" /* Scales beautifully on small phones */,
                color: "#ffffff",
                textShadow:
                  "0 0 30px rgba(239, 68, 68, 0.8), 0 0 10px rgba(255, 255, 255, 0.5)",
                lineHeight: "1",
              }}
            >
              {sosCountdown}
            </h1>
            <span
              className="text-danger fw-bold text-uppercase tracking-widest"
              style={{
                letterSpacing: "3px",
                fontSize: "min(0.85rem, 3.5vw)",
                opacity: 0.9,
              }}
            >
              Seconds Remaining
            </span>
          </div>

          {/* 2. CENTER: PREMIUM PULSING RADAR ICON */}
          {/* flex-grow-1 allows this section to absorb extra space so top and bottom stay fixed */}
          <div className="position-relative d-flex align-items-center justify-content-center flex-grow-1 w-100">
            {/* Radar Waves */}
            <div
              className="sos-radar-circle"
              style={{ animationDelay: "0s", borderWidth: "3px" }}
            ></div>
            <div
              className="sos-radar-circle"
              style={{ animationDelay: "0.6s", borderWidth: "2px" }}
            ></div>
            <div
              className="sos-radar-circle"
              style={{ animationDelay: "1.2s", borderWidth: "1px" }}
            ></div>

            {/* Core Icon */}
            <div
              className="rounded-circle d-flex align-items-center justify-content-center sos-core-icon"
              style={{
                width: "min(120px, 35vw)" /* Responsive sizing */,
                height: "min(120px, 35vw)",
                background:
                  "radial-gradient(circle, rgba(239,68,68,0.2) 0%, rgba(220,38,38,0.05) 100%)",
                border: "3px solid #ef4444",
                boxShadow:
                  "0 0 40px rgba(239, 68, 68, 0.6), inset 0 0 20px rgba(239, 68, 68, 0.3)",
                zIndex: 10,
                backdropFilter: "blur(4px)",
              }}
            >
              <Siren
                size={48}
                color="#ffffff"
                style={{ filter: "drop-shadow(0 0 8px rgba(255,255,255,0.6))" }}
              />
            </div>
          </div>

          {/* 3. BOTTOM: TEXT & ACTION */}
          <div
            className="text-center w-100 px-4 mb-2"
            style={{ maxWidth: "400px" }}
          >
            <div className="mb-4">
              <h6
                className="text-white opacity-75 fw-bold mb-2 text-uppercase"
                style={{ letterSpacing: "1.5px", fontSize: "min(0.8rem, 3vw)" }}
              >
                Sending Emergency Alert to Admin
              </h6>
              <h2
                className="text-danger fw-black sos-warning-text m-0"
                style={{
                  fontSize:
                    "min(2rem, 7vw)" /* Prevents text from breaking lines awkwardly */,
                  letterSpacing: "1px",
                  textShadow: "0 0 20px rgba(239, 68, 68, 0.4)",
                }}
              >
                SOS INITIATED
              </h2>
            </div>

            <button
              onClick={handleCancelSos}
              className="btn btn-cancel-sos w-100 rounded-pill py-3 fw-bolder text-white d-flex align-items-center justify-content-center gap-2"
              style={{ fontSize: "min(1.1rem, 4.5vw)", letterSpacing: "1px" }}
            >
              <X size={24} strokeWidth={3} />
              CANCEL ALERT
            </button>
          </div>
        </div>
      )}

      {/* === 2. TOP HEADER (Fixed) === */}
      <header
        className="navbar navbar-expand-md navbar-light bg-white sticky-top shadow-sm px-2"
        style={{
          minHeight: "64px",
          paddingTop: "env(safe-area-inset-top, 0px)",
          zIndex: 900,
        }}
      >
        <div className="container-fluid mw-1200 px-0">
          <div className="d-flex align-items-center">
            <button
              className="btn btn-white p-2 me-2 text-dark border-0"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu size={24} strokeWidth={2.5} />
            </button>
            <div
              className="d-flex align-items-baseline cursor-pointer text-decoration-none"
              onClick={() => navigate("/dashboard")}
            >
              <span
                className="fw-bold lh-1"
                style={{ fontSize: "1.5rem", color: "#fd7e14" }}
              >
                P
              </span>
              <span
                className="fw-bold lh-1"
                style={{
                  fontSize: "0.8rem",
                  marginLeft: "-1px",
                  color: "#fd7e14",
                }}
              >
                ATRA
              </span>
              <span
                className="fw-semibold text-dark lh-1"
                style={{ fontSize: "0.8rem", marginLeft: "6px" }}
              >
                TRAVELS PVT. LTD.
              </span>
            </div>
          </div>

          <div className="ms-auto d-flex align-items-center gap-1">
            {/* 🟢 NEW: INTERACTIVE NOTIFICATION BELL */}
            <button
              className="btn btn-light rounded-circle p-2 text-secondary position-relative border-0"
              style={{ width: "40px", height: "40px" }}
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell size={20} />
              {notifications.filter((n) => !n.isRead).length > 0 && (
                <span
                  className="position-absolute bg-danger border border-2 border-white rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                  style={{
                    width: "16px",
                    height: "16px",
                    top: "5px",
                    right: "5px",
                    fontSize: "9px",
                  }}
                >
                  {notifications.filter((n) => !n.isRead).length}
                </span>
              )}
            </button>

            {/*  RIGHT-SIDE NOTIFICATION DRAWER */}
            {showNotifications && (
              <>
                {/* Dark, slightly blurred backdrop to focus attention on the drawer */}
                <div
                  className="position-fixed top-0 start-0 w-100 h-100"
                  style={{
                    zIndex: 1999,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    backdropFilter: "blur(2px)",
                    animation: "fade-in-backdrop 0.25s ease-out forwards",
                  }}
                  onClick={() => setShowNotifications(false)}
                ></div>

                {/* The Drawer Panel */}
                <div
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className="bg-white shadow-lg d-flex flex-column"
                  style={{
                    position: "fixed",
                    top: 0,
                    right: 0,
                    height: "100%",
                    width: "min(85%, 360px)",
                    zIndex: 2000,
                    animation:
                      "slide-in-right 0.35s cubic-bezier(0.25, 1, 0.5, 1) forwards",
                    paddingTop: "max(env(safe-area-inset-top), 10px)",
                  }}
                >
                  {/* Drawer Header */}
                  <div
                    className="d-flex justify-content-between align-items-center p-3 border-bottom shadow-sm z-1"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.95)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="btn btn-sm btn-light rounded-circle p-1 d-flex align-items-center justify-content-center border-0"
                        onClick={() => setShowNotifications(false)}
                        style={{ background: "#f1f5f9" }}
                      >
                        <X size={22} className="text-dark" />
                      </button>
                      <h6
                        className="fw-bolder mb-0 text-dark"
                        style={{ fontSize: "1.15rem", letterSpacing: "0.2px" }}
                      >
                        Notifications
                      </h6>
                    </div>

                    {/* Action Buttons: Mark Read & Clear */}
                    <div className="d-flex gap-3 align-items-center">
                      {notifications.some((n) => !n.isRead) && (
                        <span
                          className="text-primary cursor-pointer fw-bold"
                          style={{ fontSize: "13px" }}
                          onClick={() =>
                            setNotifications(
                              notifications.map((n) => ({
                                ...n,
                                isRead: true,
                              })),
                            )
                          }
                        >
                          Mark read
                        </span>
                      )}

                      {notifications.length > 0 && (
                        <span
                          className="text-danger cursor-pointer fw-bold"
                          style={{ fontSize: "13px" }}
                          onClick={() => setNotifications([])}
                        >
                          Clear
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Notification List (Scrollable interior) */}
                  <div
                    className="flex-grow-1 overflow-auto p-2"
                    style={{
                      paddingBottom: "max(env(safe-area-inset-bottom), 20px)",
                      backgroundColor: "#fafbfc",
                    }}
                  >
                    {notifications.length === 0 ? (
                      // --- ✨ PREMIUM NATIVE EMPTY STATE ✨ ---
                      <div className="d-flex flex-column align-items-center justify-content-center h-100 pb-5">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mb-4"
                          style={{
                            width: "90px",
                            height: "90px",
                            background:
                              "linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)",
                            boxShadow:
                              "0 10px 25px rgba(0,0,0,0.05), inset 0 2px 5px rgba(255,255,255,0.8)",
                            border: "1px solid rgba(255,255,255,0.6)",
                            animation: "float-bell 3s ease-in-out infinite",
                          }}
                        >
                          <Bell size={42} color="#a0aec0" strokeWidth={1.5} />
                        </div>
                        <h5
                          className="fw-bolder mb-2"
                          style={{ color: "#2d3748", letterSpacing: "0.2px" }}
                        >
                          All caught up!
                        </h5>
                        <p
                          className="text-center px-4"
                          style={{
                            color: "#718096",
                            fontSize: "14px",
                            lineHeight: "1.5",
                            fontWeight: "500",
                          }}
                        >
                          You have no new notifications right now.
                        </p>
                      </div>
                    ) : (
                      notifications.map((note) => (
                        <div
                          key={note.id}
                          className={`p-3 rounded-4 mb-2 shadow-sm border ${
                            note.isRead
                              ? "bg-white border-light"
                              : "bg-white border-primary border-opacity-25"
                          }`}
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.1s ease",
                            transform: "scale(1)",
                          }}
                          onMouseDown={(e) =>
                            (e.currentTarget.style.transform = "scale(0.98)")
                          }
                          onMouseUp={(e) =>
                            (e.currentTarget.style.transform = "scale(1)")
                          }
                          onClick={() => {
                            setShowNotifications(false);
                            setNotifications(
                              notifications.map((n) =>
                                n.id === note.id ? { ...n, isRead: true } : n,
                              ),
                            );
                            if (note.type === "trip" && note.bookingId)
                              navigate(`/my-trips/${note.bookingId}`);
                            else if (note.type === "attendance")
                              navigate(`/dashboard`);
                          }}
                        >
                          <div className="d-flex align-items-start gap-3">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                              style={{
                                width: "42px",
                                height: "42px",
                                minWidth: "42px",
                                background:
                                  note.type === "attendance"
                                    ? "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)"
                                    : "linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%)",
                              }}
                            >
                              <span style={{ fontSize: "20px" }}>
                                {note.type === "attendance" ? "⏰" : "🚗"}
                              </span>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div
                                  className="fw-bolder text-dark"
                                  style={{ fontSize: "14px" }}
                                >
                                  {note.title}
                                </div>
                                {!note.isRead && (
                                  <div
                                    className="bg-primary rounded-circle mt-1 shadow-sm"
                                    style={{ width: "8px", height: "8px" }}
                                  ></div>
                                )}
                              </div>
                              <div
                                className="fw-normal text-secondary mt-1"
                                style={{ fontSize: "13px", lineHeight: "1.4" }}
                              >
                                {note.message}
                              </div>
                              <div
                                className="text-muted mt-2 fw-bold"
                                style={{
                                  fontSize: "11px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {new Date(note.time).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            <button
              className="btn btn-danger rounded-circle p-2 d-flex align-items-center justify-content-center border-0 ms-1 shadow-sm sos-pulse-always"
              onClick={handleSosTrigger}
              style={{ width: "42px", height: "42px" }}
            >
              <Siren size={22} fill="white" />
            </button>
            <button
              className="btn btn-light rounded-circle p-2 text-danger border-0 "
              onClick={handleLogout}
              style={{ width: "40px", height: "40px" }}
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* === 3. MAIN CONTENT === */}
      <main className="container-fluid bg-white flex-grow-1 py-2 px-2">
        <div
          className="mx-auto"
          style={{ maxWidth: "1200px", paddingBottom: "60px" }}
        >
          <Outlet />
        </div>
      </main>

      {/* === 4. BOTTOM NAV (Mobile Only) === */}
      <nav
        className="navbar fixed-bottom navbar-light bg-white d-md-none shadow-lg"
        style={{ paddingBottom: "env(safe-area-inset-bottom)", zIndex: 800 }}
      >
        <div
          className="container-fluid d-flex justify-content-around w-100 px-0"
          style={{
            borderRadius: "10px 10px 0 0",
          }}
        >
          {bottomNavItems.map((item) => (
            <div
              key={item.path}
              onClick={() => handleRestrictedNavigate(item)}
              className="d-flex flex-column align-items-center justify-content-center py-2 flex-grow-1 cursor-pointer"
              style={{ height: "60px" }}
            >
              <item.icon
                size={24}
                /* FIX 1: Pass item.path directly */
                strokeWidth={isActive(item.path) ? 2.5 : 2}
                className={
                  /* FIX 2: Pass item.path directly */
                  isActive(item.path)
                    ? "text-primary"
                    : "text-secondary opacity-50"
                }
              />
              <span
                className={`mt-1 fw-bold ${
                  /* FIX 3: Pass item.path directly */
                  isActive(item.path) ? "text-primary" : "text-dark opacity-50"
                }`}
                style={{ fontSize: "11px" }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default HeaderLayout;